import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Paper,
} from "@mui/material";
import { useDispatchAction } from "../../../../redux/store.hooks";
import { useBillingCompanyContext } from "../../../../providers";
import { fetchColumnsByTableNameAsync } from "../../../../redux/common/commonActions";
import { IHeaderMapping } from "../../../market-campaign/components/create-new-audience/CreateAudienceRightPanel.types";
import { ExtLeadStatusSearch } from "../../../chatLeads/selectors/ExtLeadStatusSearch";
import { ExternalPriorityFilter } from "../../../../components/AutoCompleteSearches/ExternalPriorityFilter";

export type IStep2SetupMappingLeadsProps = {
  headers: string[];
  data: Record<string, any>[];
  mappings: IHeaderMapping[];
  onMappingChange: (mappings: IHeaderMapping[]) => void;
  statusValues: {
    status: string;
    actual_status: string;
    drive_status_uuid: string;
    priority?: string;
  };
  onStatusChange: (statusData: {
    status: string;
    actual_status: string;
    drive_status_uuid: string;
    priority?: string;
  }) => void;
  statusError?: string;
  priorityError?: string;
  tableName?: string;
};

const formatColumnLabel = (str: string): string => {
  if (!str) return "";
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const MANDATORY_PHONE_FIELDS = ["contact_number_one"];
export const MANDATORY_FIELDS = MANDATORY_PHONE_FIELDS;

export const isMandatoryPhoneField = (field: string): boolean => {
  if (!field) return false;
  return field.toLowerCase() === "contact_number_one";
};

export const isExcludedColumn = (colName: string): boolean => {
  if (!colName) return true;
  const lower = colName.toLowerCase();
  return (
    lower.endsWith("_id") ||
    lower.includes("_ts") ||
    lower.includes("by_name") ||
    lower.includes("_uuid") ||
    lower.includes("is_archived") ||
    lower.includes("is_default_event") ||
    lower.includes("source") ||
    lower.includes("to_name")
  );
};

export const Step2SetupMappingLeads: React.FC<IStep2SetupMappingLeadsProps> = (
  props,
) => {
  const {
    headers,
    data,
    mappings: headerMappings,
    onMappingChange,
    statusValues,
    onStatusChange,
    statusError,
    priorityError,
    tableName,
  } = props;

  const dispatch = useDispatchAction();
  const companyOutlet = useBillingCompanyContext();
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [availableColumns, setAvailableColumns] = useState<string[]>([
    "contact_number_one",
  ]);

  // Fetch columns from VIEW action_item
  useEffect(() => {
    if (companyOutlet?.comapny?.company_outlet_uuid) {
      dispatch(
        fetchColumnsByTableNameAsync(
          "VIEW",
          tableName || "latest_leads",
          (columnsData: any) => {
            if (columnsData && Array.isArray(columnsData)) {
              const filteredColumnNames: string[] = columnsData
                .filter((col: any) => {
                  const colName =
                    typeof col === "string" ? col : col?.COLUMN_NAME || "";
                  const dataType =
                    typeof col === "object" ? col?.DATA_TYPE || "" : "";

                  if (dataType.toLowerCase() === "json") {
                    return false;
                  }
                  if (isExcludedColumn(colName)) {
                    return false;
                  }
                  return Boolean(colName);
                })
                .map((col: any) =>
                  typeof col === "string" ? col : col.COLUMN_NAME,
                );

              if (!filteredColumnNames.some((c) => isMandatoryPhoneField(c))) {
                filteredColumnNames.unshift("contact_number_one");
              }

              setAvailableColumns(filteredColumnNames);
            }
          },
          companyOutlet.comapny.company_outlet_uuid,
          {
            is_data_type_required: true,
          },
        ),
      );
    }
  }, [dispatch, companyOutlet?.comapny?.company_outlet_uuid, tableName]);

  // Initialize header mappings when uploaded headers or available columns change
  useEffect(() => {
    if (
      headers.length > 0 &&
      availableColumns.length > 0 &&
      (headerMappings.length === 0 || !isInitialized)
    ) {
      const existingHeaders = new Set(headerMappings.map((m) => m.header));
      const newHeaders = headers.filter(
        (header) => !existingHeaders.has(header),
      );

      if (newHeaders.length > 0) {
        const newMappings: IHeaderMapping[] = newHeaders.map((header) => {
          const lowerHeader = header.trim().toLowerCase();
          let matchingColumn = availableColumns.find(
            (col) => col.trim().toLowerCase() === lowerHeader,
          );

          // If no exact match, check phone / contact aliases
          if (!matchingColumn) {
            const isPhoneAlias = [
              "mobile",
              "mobile_no",
              "mobile no",
              "contact",
              "contact_no",
              "contact no",
              "contact_number",
              "contact number",
              "contact_number_one",
              "phone",
              "phone_no",
              "phone no",
              "phone_number",
            ].includes(lowerHeader);

            if (isPhoneAlias) {
              matchingColumn = availableColumns.find(
                (col) => col.toLowerCase() === "contact_number_one",
              );
            }
          }

          return {
            header,
            selectedMapping: matchingColumn ? matchingColumn : "",
            isChecked: !!matchingColumn,
            isMapped: !!matchingColumn,
          };
        });

        onMappingChange([...headerMappings, ...newMappings]);
      }

      setIsInitialized(true);
    }
  }, [
    headers,
    availableColumns,
    headerMappings,
    isInitialized,
    onMappingChange,
  ]);

  // Separate mapped and unmapped headers
  const mappedHeaders = headerMappings.filter((mapping) => mapping.isMapped);
  const unmappedHeaders = headerMappings.filter((mapping) => !mapping.isMapped);

  // Get available options for dropdown (exclude already mapped options and sort mandatory options to top)
  const getAvailableOptions = (currentMapping: IHeaderMapping) => {
    const alreadyMappedOptions = headerMappings
      .filter(
        (mapping) =>
          mapping.isMapped &&
          mapping.selectedMapping &&
          mapping.header !== currentMapping.header,
      )
      .map((mapping) => mapping.selectedMapping);

    const available = availableColumns.filter(
      (column) =>
        !alreadyMappedOptions.includes(column) && !isExcludedColumn(column),
    );

    if (
      !alreadyMappedOptions.some((c) => isMandatoryPhoneField(c)) &&
      !available.some((c) => isMandatoryPhoneField(c))
    ) {
      available.unshift("contact_number_one");
    }

    return available.sort((a, b) => {
      const isAMandatory = isMandatoryPhoneField(a);
      const isBMandatory = isMandatoryPhoneField(b);
      if (isAMandatory && !isBMandatory) return -1;
      if (!isAMandatory && isBMandatory) return 1;
      return formatColumnLabel(a).localeCompare(formatColumnLabel(b));
    });
  };

  const getUnmappedMandatoryFields = () => {
    const isContactOneMapped = headerMappings.some(
      (mapping) =>
        mapping.isMapped &&
        mapping.isChecked &&
        mapping.selectedMapping?.toLowerCase() === "contact_number_one",
    );

    if (isContactOneMapped) {
      return [];
    }

    return ["contact_number_one"];
  };

  const unmappedMandatoryFields = getUnmappedMandatoryFields();
  const hasUnmappedMandatoryFields = unmappedMandatoryFields.length > 0;

  const handleCheckboxChange = (index: number, isMapped: boolean) => {
    const targetHeaders = isMapped ? mappedHeaders : unmappedHeaders;

    onMappingChange(
      headerMappings.map((mapping) => {
        const targetIndex = targetHeaders.findIndex(
          (m) => m.header === mapping.header,
        );
        return targetIndex === index
          ? { ...mapping, isChecked: !mapping.isChecked }
          : mapping;
      }),
    );
  };

  const handleDropdownChange = (
    index: number,
    value: string,
    isMapped: boolean,
  ) => {
    const targetHeaders = isMapped ? mappedHeaders : unmappedHeaders;

    onMappingChange(
      headerMappings.map((mapping) => {
        const targetIndex = targetHeaders.findIndex(
          (m) => m.header === mapping.header,
        );
        return targetIndex === index
          ? {
              ...mapping,
              selectedMapping: value,
              isMapped: !!value,
              isChecked: !!value,
            }
          : mapping;
      }),
    );
  };

  const renderHeaderTable = (
    headersList: IHeaderMapping[],
    title: string,
    isMapped: boolean,
    dataList: Record<string, any>[],
  ) => (
    <>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 1.5, mt: 1 }}>
        {title}
      </Typography>
      <Paper sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 2, mb: 2 }}>
        <Grid container spacing={2}>
          {/* Left Column - Uploaded Headers */}
          <Grid item xs={4}>
            <Typography variant="body2" fontWeight="700" sx={{ mb: 1 }}>
              Uploaded Headers
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {headersList.map((mapping) => (
                <Box
                  key={mapping.header}
                  sx={{
                    p: 1,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    backgroundColor: "#f9f9f9",
                    height: "56px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" fontWeight="medium">
                    {mapping.header}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Middle Column - Headers Data */}
          <Grid item xs={4}>
            <Typography
              variant="body2"
              textAlign="center"
              fontWeight="700"
              sx={{ mb: 1 }}
            >
              Headers Data
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {headersList.map((mapping) => {
                const keyData = dataList
                  .map((item) => item[mapping.header])
                  .slice(0, 2);
                return (
                  <Box
                    key={mapping.header}
                    sx={{
                      p: 1,
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      backgroundColor: "#f9f9f9",
                      height: "56px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {keyData.map((keys, idx) => (
                      <Typography
                        key={idx}
                        variant="caption"
                        fontWeight="medium"
                        color="text.secondary"
                        noWrap
                      >
                        {keys !== undefined && keys !== null
                          ? String(keys)
                          : "--"}
                      </Typography>
                    ))}
                  </Box>
                );
              })}
            </Box>
          </Grid>

          {/* Right Column - Mapping Controls */}
          <Grid item xs={4}>
            <Typography variant="body2" fontWeight="700" sx={{ mb: 1 }}>
              Mapping Controls
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {headersList.map((mapping, index) => (
                <Box
                  key={`mapping-${mapping.header}`}
                  sx={{
                    p: 1,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    backgroundColor: "#ffffff",
                    height: "56px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={mapping.isChecked}
                        onChange={() => handleCheckboxChange(index, isMapped)}
                        size="small"
                        disabled={!mapping.selectedMapping}
                        sx={{
                          cursor: !mapping.selectedMapping
                            ? "not-allowed"
                            : "pointer",
                          opacity: !mapping.selectedMapping ? 0.5 : 1,
                        }}
                      />
                    }
                    label=""
                    sx={{ m: 0, minWidth: "auto" }}
                  />

                  <FormControl fullWidth size="small">
                    <Select
                      value={mapping.selectedMapping}
                      onChange={(e) =>
                        handleDropdownChange(
                          index,
                          e.target.value as string,
                          isMapped,
                        )
                      }
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <em>
                              {getAvailableOptions(mapping).length > 0
                                ? "Select mapping"
                                : "No columns available"}
                            </em>
                          );
                        }
                        return formatColumnLabel(selected as string);
                      }}
                      sx={{ minWidth: "180px" }}
                    >
                      <MenuItem value="">
                        <em>
                          {getAvailableOptions(mapping).length > 0
                            ? "Select mapping"
                            : "No columns available"}
                        </em>
                      </MenuItem>
                      {getAvailableOptions(mapping).map((column) => {
                        const isMandatory = isMandatoryPhoneField(column);
                        return (
                          <MenuItem
                            key={column}
                            value={column}
                            sx={{
                              backgroundColor: isMandatory
                                ? "#fff3cd"
                                : "inherit",
                              "&:hover": {
                                backgroundColor: isMandatory
                                  ? "#ffeaa7"
                                  : "inherit",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {isMandatory && (
                                <Typography
                                  variant="caption"
                                  color="warning.dark"
                                  fontWeight="bold"
                                >
                                  ⚠️
                                </Typography>
                              )}
                              <Typography
                                variant="body2"
                                fontWeight={isMandatory ? "bold" : "normal"}
                                color={isMandatory ? "warning.dark" : "inherit"}
                              >
                                {formatColumnLabel(column)}
                              </Typography>
                              {isMandatory && (
                                <Typography
                                  variant="caption"
                                  color="warning.dark"
                                  fontWeight="medium"
                                >
                                  (Required)
                                </Typography>
                              )}
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </>
  );

  return (
    <Box sx={{ width: "100%" }}>
      {/* Top Section: Status & Priority Selection */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          backgroundColor: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={700}
          color="primary.main"
          mb={1}
        >
          Default Lead Settings (Applied to all uploaded leads) *
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Select status and priority to apply to all uploaded leads in this
          batch. Both fields are required.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <ExtLeadStatusSearch
              value={statusValues.actual_status || null}
              statusName={statusValues.status || ""}
              // placeholder="Select Lead Status"
              error={statusError}
              onChange={(newValue: any) => {
                if (newValue) {
                  onStatusChange({
                    ...statusValues,
                    status: newValue.name || "",
                    actual_status: newValue.actual_status || "",
                    drive_status_uuid:
                      newValue.external_status_uuid ||
                      (newValue as any).drive_status_uuid ||
                      "",
                  });
                } else {
                  onStatusChange({
                    ...statusValues,
                    status: "",
                    actual_status: "",
                    drive_status_uuid: "",
                  });
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <ExternalPriorityFilter
              value={statusValues.priority || ""}
              // placeholder="Select Priority"
              error={priorityError}
              onChange={(val) => {
                onStatusChange({
                  ...statusValues,
                  priority: val,
                });
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Mandatory Fields Warning */}
      {hasUnmappedMandatoryFields && (
        <Box
          sx={{
            p: 2,
            mb: 2,
            backgroundColor: "#fff3cd",
            border: "1px solid #ffeaa7",
            borderRadius: 2,
            borderLeft: "4px solid #f39c12",
          }}
        >
          <Typography variant="body2" color="warning.dark" fontWeight="medium">
            ⚠️ Required Mapping: Please map the following mandatory fields:{" "}
            <strong>
              {unmappedMandatoryFields.map(formatColumnLabel).join(", ")}
            </strong>
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: "block" }}
          >
            These fields are required for lead processing and cannot be skipped.
          </Typography>
        </Box>
      )}

      {/* Mapped Headers Table */}
      {mappedHeaders.length > 0 &&
        renderHeaderTable(mappedHeaders, "Mapped Headers", true, data)}

      {/* Unmapped Headers Table */}
      {unmappedHeaders.length > 0 &&
        renderHeaderTable(unmappedHeaders, "Unmapped Headers", false, data)}
    </Box>
  );
};
